import React, { useEffect, useMemo, useState } from "react";
import "./order-details.scss";
import { Box, Button, Table, Typography } from "@mui/material";
import EqualWidthList from "../../../../../common/components/equal-width-list/EqualWidthList";
import { useLocation, useNavigate } from "react-router-dom";
import { useResourcePermissions } from "../../../../../common/hooks/resource-permissions";
import MODULES from "../../../../../constants/modules";
import MUIBoxTable from "../../../../../common/components/mui-table/mui-box-table";
import { getOrderById } from "../../../../../api/order-management";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import {
  formatDate,
  formatDateTime,
  formatTitleCase,
} from "../../../../../utils/utils";
import { capitalize } from "lodash";
import {
  getOrderInvoiceHeaders,
  ordersBreadcrumbLink,
} from "../../order-management-helper";
import { routes } from "../../../../../constants/routes";
import { getTenantDetails } from "../../../../../api/tenets";
import InvoiceDetails from "./view-invoice-details";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../../../common/components";
import eyeIcon from "../../../../../assets/icons/eye-light.svg";
import CustomTooltip from "../../../../../common/components/custom-tooltip";

const OrderDetail = ({}) => {
  const headers = getOrderInvoiceHeaders();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { ordersDetails } = state || {};
  const orderId = ordersDetails?.id;

  const handleUnlimited = (value: any) => {
    return value == -1 ? "Unlimited" : value;
  };
  const orderDetailssBreadcrumbLink = [
    {
      label: "Orders",
      link: routes.ORDERSLIST,
      handleClick: (event: any) => {
        event.preventDefault();
        console.log("Orders clicked");
        navigate(routes.ORDERSLIST);
      },
    },
    {
      label: "Order Details",
      link: routes.ORDER_DETAILS,
      handleClick: () => {},
    },
  ];
  const { canWrite } = useResourcePermissions(MODULES.ORDER_MANAGEMENT);
  const path = routes.ORDER_DETAILS_CHANGE_PLAN;
  const [loading, setLoading] = useState<boolean>(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [tenantData, setTenantData] = useState<any>(null);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState<boolean>(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );
  const [crumbleMappingOption, setCrumbMappingOption] = useState<any>(
    orderDetailssBreadcrumbLink,
  );
  const [previewOnly, setPreviewOnly] = useState<boolean>(false);
  const [isOnboardingPending, setIsOnboardingPending] =
    useState<boolean>(false);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});

  const fetchOrderDetails = async (orderId: number) => {
    setLoading(true);
    try {
      const response = await getOrderById(orderId);
      setOrderDetails(response?.data?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails(orderId);
    }
  }, [orderId]);

  const onInvoiceClick = (invoiceId: number, pdfPreview: boolean = false) => {
    setSelectedInvoiceId(invoiceId);
    setIsInvoiceOpen(true);
    setPreviewOnly(pdfPreview);

    setCrumbMappingOption([
      {
        label: "Orders",
        link: "#",
      },
      {
        label: "Order Details",
        link: "#",
        handleClick: (e: any) => {
          e.preventDefault();
          setIsInvoiceOpen(false);
          setCrumbMappingOption(orderDetailssBreadcrumbLink);
        },
      },
      {
        label: "Invoice Detail",
        link: "",
      },
    ]);
  };

  const fetchTenantData = async (tenantId: number) => {
    if (!tenantId) return;

    try {
      setLoading(true);
      const response = await getTenantDetails({ id: tenantId });
      setTenantData(response?.data?.data);
    } catch (error: any) {
      console.log("error", error?.response?.data?.message);
      if (
        error?.response?.data?.message.includes("No database details found ")
      ) {
        setIsOnboardingPending(true);
      }
      console.error("Error fetching tenant details:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (orderDetails?.tenantId) {
      fetchTenantData(orderDetails.tenantId);
    }
  }, [orderDetails]);

  const handleLeadsContacts = (leadsValue: any, contactsValue: any) => {
    const leads = handleUnlimited(leadsValue);
    const contacts = handleUnlimited(contactsValue);
    if (leads === contacts) {
      return leads;
    }
    return `${leads} & ${contacts}`;
  };

  function formatCurrencyWithLabel(
    value: string | number | null | undefined,
  ): string {
    if (!value) return "-";

    let amount =
      typeof value === "string" ? value.trim() : value.toString().trim();
    const match = amount.match(/^(\d+(?:\.\d+)?)(?:\s*(\w+))?$/i);

    if (!match) return value.toString(); // fallback in case format doesn't match

    const numericValue = parseFloat(match[1]);
    const label = match[2]
      ? match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase()
      : null;

    const formattedCurrency = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(numericValue);

    return label ? `${formattedCurrency} (${label})` : formattedCurrency;
  }

  const formattedData = useMemo(() => {
    return orderDetails?.invoices
      ? orderDetails.invoices.map((invoice: any) => ({
          invoiceId: invoice.invoice_number,
          dataTime: formatDateTime(invoice.invoice_date),
          status: capitalize(invoice.status),
          action: (
            <div className="order-invoice-action">
              <p onClick={() => onInvoiceClick(invoice.id)}>View</p>
              <CustomTooltip title="View Invoice Pdf" placement="bottom">
                <img
                  src={eyeIcon}
                  alt="view"
                  onClick={() => onInvoiceClick(invoice.id, true)}
                />
              </CustomTooltip>
            </div>
          ),
        }))
      : [];
  }, [orderDetails]);

  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading Invoices...";
    }
    return "No invoices available.";
  }, [loading]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="breadcrumb-wrapper">
        <BreadcrumbComponent
          breadcrumbLink={crumbleMappingOption}
          showBackArrow
          handleBackArrowClick={() => navigate(routes.ORDERSLIST)}
        />
      </div>
      {isInvoiceOpen ? (
        <InvoiceDetails
          invoiceId={selectedInvoiceId}
          previewOnly={previewOnly}
        />
      ) : (
        <div className="order-detail-wrapper">
          <div className="order-details-container">
            <div className="order-detail-header">
              <h2>Details</h2>
            </div>
            <div className="order-detail-content">
              <div className="order-detail-info">
                <div className="order-detail-item">
                  <p className="label">Customer</p>
                  <p className="value">{orderDetails?.customer_name}</p>
                </div>
                <div className="order-detail-item">
                  <p className="label">Payment Status</p>
                  <p className="value">
                    {capitalize(orderDetails?.payment_status)}
                  </p>
                </div>
                <div className="order-detail-item">
                  <p className="label">Created On</p>
                  <p className="value">
                    {orderDetails?.created_at
                      ? formatDate(orderDetails?.created_at)
                      : "-"}
                  </p>
                </div>
                <div className="order-detail-item">
                  <p className="label">Order ID</p>
                  <p className="value">{orderDetails?.order_number}</p>
                </div>
              </div>
              <div className="order-detail-info">
                <div className="order-detail-item">
                  <p className="label">Order Status</p>
                  <p className="value">
                    {capitalize(orderDetails?.order_status)}
                  </p>
                </div>
                <div className="order-detail-item">
                  <p className="label">Total Amount</p>
                  <p className="value">
                    {formatCurrencyWithLabel(orderDetails?.total_amount)}
                  </p>
                </div>
                <div className="order-detail-item">
                  <p className="label">Balance Amount</p>
                  <p className="value">
                    {formatCurrencyWithLabel(orderDetails?.balanced_amount)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="order-detail-plan-wrapper">
            <Box className="order-detail-plan-header">
              <h2>Plan Details</h2>
              {canWrite && (
                <Button
                  variant="outlined"
                  className="order-detail-plan-header-button"
                  onClick={() => {
                    if (isOnboardingPending) {
                      setAlert({
                        isOpen: true,
                        message: (
                          <Typography sx={{ fontSize: "13px" }}>
                            <span
                              style={{
                                fontWeight: "bold",
                                fontSize: "16px",
                              }}
                            >
                              Partner Onboard Pending.
                            </span>
                            <br />
                            Please ensure payment is completed to change the
                            plan.
                          </Typography>
                        ),

                        type: "error",
                      });
                    } else {
                      navigate(path, {
                        state: {
                          activePlanDetails:
                            orderDetails?.purchased_plan_details,
                          tenantData: tenantData,
                        },
                      });
                    }
                  }}
                >
                  Change Plan
                </Button>
              )}
            </Box>
            <Box className="order-detail-plan-content" gap={2}>
              <Box className="order-detail-plan-content-header">
                <EqualWidthList
                  labelValue={orderDetails?.purchased_plan_details?.plan_name}
                  label="Plan"
                  className="plan-button-wrapperr"
                  enableTooltip
                />
                <EqualWidthList
                  labelValue={capitalize(
                    orderDetails?.purchased_plan_details?.frequency,
                  )}
                  label="Frequency"
                />
                <EqualWidthList
                  labelValue={handleUnlimited(
                    orderDetails?.purchased_plan_details?.no_of_users,
                  )}
                  label="Users Limit"
                />
                <EqualWidthList
                  labelValue={
                    orderDetails?.purchased_plan_details?.storage_limits
                  }
                  label="Data Storage Limit"
                />
                <EqualWidthList
                  labelValue={handleUnlimited(
                    orderDetails?.purchased_plan_details
                      ?.automation_rules_limit,
                  )}
                  label="Automation Rules"
                />
              </Box>
              <Box className="order-detail-plan-content-header">
                <EqualWidthList
                  label="Leads and Contact"
                  labelValue={handleLeadsContacts(
                    orderDetails?.purchased_plan_details?.no_of_leads,
                    orderDetails?.purchased_plan_details?.no_of_contacts,
                  )}
                />{" "}
                <EqualWidthList
                  labelValue={handleUnlimited(
                    orderDetails?.purchased_plan_details?.api_calls_per_month,
                  )}
                  label="API Calls"
                />
                <EqualWidthList
                  labelValue={formatTitleCase(
                    orderDetails?.purchased_plan_details?.support_level,
                  )}
                  label="Support"
                />
                <EqualWidthList
                  labelValue={formatTitleCase(
                    orderDetails?.purchased_plan_details?.support_level,
                  )}
                  label="Reports"
                />{" "}
              </Box>
            </Box>
          </div>
          <div className="order-invoice-wrapper">
            <div className="order-invoice-header">
              <h2>Invoice Generated ({orderDetails?.invoices?.length})</h2>
            </div>
            <div className="order-invoice-content">
              <MUIBoxTable
                columns={headers}
                data={formattedData}
                noResultText={noResultsTxt}
              />
            </div>
          </div>
        </div>
      )}
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </>
  );
};

export default OrderDetail;
